import SearchableSelect from '../components/search/SearchableSelect';
import CollectionSearch from '../components/search/CollectionSearch';
import { filterRecords } from '../utils/search';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Typography, Paper, Box, TextField, Button, IconButton,
  MenuItem, FormControl, InputLabel, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Alert, Chip, ToggleButton,
  ToggleButtonGroup, LinearProgress, Checkbox, FormControlLabel, Divider,
  List, ListItem, ListItemText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import {
  getAccounts, getCategories, getPurchaseCategoryBalances,
  createPurchaseBatch, getPurchaseBatches, undoPurchaseBatch
} from '../services/api';
import AIChatButton from '../components/ai/AIChatButton';

const todayStr = () => new Date().toISOString().split('T')[0];
const fmt = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Resolve rows to cent amounts. Percent rows use largest-remainder distribution so
// the resolved percent total matches round(total * pctSum) exactly.
function resolveRows(rows, totalCents) {
  const resolved = rows.map((row) => {
    if (row.mode === 'fixed') {
      const cents = Math.round(parseFloat(row.value || '0') * 100);
      return { ...row, cents: isNaN(cents) ? 0 : cents, frac: 0 };
    }
    const pct = parseFloat(row.value || '0');
    const raw = (isNaN(pct) ? 0 : pct) * totalCents / 100;
    return { ...row, cents: Math.floor(raw), frac: raw - Math.floor(raw) };
  });
  const pctRows = resolved.filter(r => r.mode === 'percent');
  const pctTarget = Math.round(pctRows.reduce((s, r) => s + (parseFloat(r.value || '0') || 0), 0) * totalCents / 100);
  let leftover = pctTarget - pctRows.reduce((s, r) => s + r.cents, 0);
  const byFrac = [...pctRows].sort((a, b) => b.frac - a.frac);
  for (let i = 0; leftover > 0 && byFrac.length > 0; i = (i + 1) % byFrac.length) {
    byFrac[i].cents += 1;
    leftover -= 1;
  }
  return resolved;
}

function LargePurchasePage() {
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [balances, setBalances] = useState({});
  const [batches, setBatches] = useState([]);
  const [historySearch, setHistorySearch] = useState('');
  const visibleBatches = filterRecords(batches, historySearch, batch => [batch.label, batch.total_amount, batch.transaction_date])
    .slice().sort((left, right) => (right.transaction_date || '').localeCompare(left.transaction_date || ''));

  const [label, setLabel] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [date, setDate] = useState(todayStr());
  const [falloverCategoryId, setFalloverCategoryId] = useState('');
  const [payerAccountId, setPayerAccountId] = useState('');
  const [payerCategoryId, setPayerCategoryId] = useState('');
  const [rows, setRows] = useState([{ key: 1, categoryId: '', mode: 'fixed', value: '', rowType: 'deduct' }]);
  const [nextKey, setNextKey] = useState(2);

  const [confirmNegative, setConfirmNegative] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const agentContext = useMemo(() => ({
    page: 'large_purchase',
    purpose: 'Explore and validate ways to fund a large purchase across the full financial network',
    isComplete: true,
    currentValues: {
      draft_label: label || null,
      draft_total_amount: totalAmount || null,
      draft_date: date || null,
    },
  }), [label, totalAmount, date]);

  const loadStatic = async () => {
    try {
      const [accRes, catRes, balRes, batchRes] = await Promise.all([
        getAccounts(), getCategories(), getPurchaseCategoryBalances(), getPurchaseBatches()
      ]);
      setAccounts(accRes.data);
      setCategories(catRes.data);
      setBalances(balRes.data);
      setBatches(batchRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load accounts/categories');
    }
  };

  useEffect(() => { loadStatic(); }, []);

  const accountName = (id) => accounts.find(a => a.id === id)?.name || `Account ${id}`;

  // Categories grouped by account for the pickers
  const groupedCategories = useMemo(() => {
    const groups = [];
    accounts.forEach(acc => {
      const cats = categories.filter(c => c.account_id === acc.id && !c.is_deleted);
      if (cats.length) groups.push({ account: acc, cats });
    });
    return groups;
  }, [accounts, categories]);

  const totalCents = Math.round(parseFloat(totalAmount || '0') * 100) || 0;
  const resolved = useMemo(() => resolveRows(rows, totalCents), [rows, totalCents]);
  const allocatedCents = resolved.reduce((s, r) => s + (r.categoryId ? r.cents : 0), 0);
  const remainingCents = totalCents - allocatedCents;

  const falloverSet = falloverCategoryId !== '';
  const exact = totalCents > 0 && remainingCents === 0;
  const falloverAbsorbs = totalCents > 0 && remainingCents > 0 && falloverSet;
  const hasTransferRows = rows.some(r => r.rowType === 'transfer');
  const payerReady = !hasTransferRows || (payerAccountId !== '' && payerCategoryId !== ''
    && !rows.some(r => r.rowType === 'transfer' && Number(r.categoryId) === Number(payerCategoryId)));
  const canApply = (exact || falloverAbsorbs)
    && label.trim() !== ''
    && resolved.every(r => r.categoryId !== '' && r.cents > 0)
    && new Set(rows.map(r => r.categoryId)).size === rows.length
    && payerReady;

  // Preview lines: resolved rows plus the projected fallover line
  const previewLines = useMemo(() => {
    const lines = resolved.filter(r => r.categoryId).map(r => ({
      categoryId: r.categoryId, cents: r.cents, isFallover: false,
      rowType: r.rowType || 'deduct'
    }));
    if (falloverAbsorbs) {
      const existing = lines.find(l => l.categoryId === Number(falloverCategoryId) || l.categoryId === falloverCategoryId);
      if (existing) existing.cents += remainingCents;
      else lines.push({ categoryId: falloverCategoryId, cents: remainingCents, isFallover: true, rowType: 'deduct' });
    }
    const mapped = lines.map(l => {
      const cat = categories.find(c => c.id === Number(l.categoryId));
      const before = parseFloat(balances[String(l.categoryId)] ?? '0');
      const after = before - l.cents / 100;
      return { ...l, cat, before, after, negative: after < 0 };
    });
    // payer summary line: receives the transfers, books the one real payment — net 0
    const transferCents = mapped.filter(l => l.rowType === 'transfer').reduce((sum, l) => sum + l.cents, 0);
    if (transferCents > 0 && payerCategoryId !== '') {
      const cat = categories.find(c => c.id === Number(payerCategoryId));
      const before = parseFloat(balances[String(payerCategoryId)] ?? '0');
      mapped.push({ categoryId: payerCategoryId, cents: transferCents, isFallover: false,
        rowType: 'payer', cat, before, after: before, negative: false });
    }
    return mapped;
  }, [resolved, falloverAbsorbs, remainingCents, falloverCategoryId, balances, categories, payerCategoryId]);

  const hasNegative = previewLines.some(l => l.negative);

  const updateRow = (key, patch) => setRows(rows.map(r => r.key === key ? { ...r, ...patch } : r));
  const addRow = () => { setRows([...rows, { key: nextKey, categoryId: '', mode: 'fixed', value: '', rowType: 'deduct' }]); setNextKey(nextKey + 1); };
  const removeRow = (key) => setRows(rows.filter(r => r.key !== key));

  const handleApply = async () => {
    setError('');
    setSubmitting(true);
    try {
      const payload = {
        label: label.trim(),
        total_amount: (totalCents / 100).toFixed(2),
        transaction_date: date,
        fallover_category_id: falloverSet ? Number(falloverCategoryId) : null,
        payer_account_id: hasTransferRows ? Number(payerAccountId) : null,
        payer_category_id: hasTransferRows ? Number(payerCategoryId) : null,
        allocations: resolved.filter(r => r.categoryId).map(r => ({
          category_id: Number(r.categoryId),
          amount: (r.cents / 100).toFixed(2),
          mode: r.rowType || 'deduct'
        }))
      };
      const res = await createPurchaseBatch(payload);
      setSuccess(res.data);
      setLabel(''); setTotalAmount(''); setFalloverCategoryId('');
      setRows([{ key: nextKey, categoryId: '', mode: 'fixed', value: '', rowType: 'deduct' }]);
      setPayerAccountId(''); setPayerCategoryId('');
      setNextKey(nextKey + 1);
      setConfirmNegative(false);
      loadStatic();
    } catch (err) {
      console.error('Apply failed:', err);
      setError(err.response?.data?.msg || 'Failed to apply the purchase');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUndo = async (batchId) => {
    setError('');
    try {
      await undoPurchaseBatch(batchId);
      setSuccess(null);
      loadStatic();
    } catch (err) {
      console.error('Undo failed:', err);
      setError(err.response?.data?.msg || 'Failed to undo the batch');
    }
  };

  const assignRemainderTo = (key) => {
    const row = rows.find(r => r.key === key);
    const rr = resolved.find(r => r.key === key);
    if (!row || !rr) return;
    const newCents = rr.cents + remainingCents;
    updateRow(key, { mode: 'fixed', value: (newCents / 100).toFixed(2) });
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h4" gutterBottom>Large Purchase Distributor</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Distribute one large purchase across categories from any of your accounts.
        Apply creates the whole batch of spending transactions in one step — undoable as a group.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" startIcon={<UndoIcon />} onClick={() => handleUndo(success.id)}>Undo</Button>}
          onClose={() => setSuccess(null)}>
          Applied "{success.label}" — {success.transactions.length} transactions created for {fmt(success.total_amount)}.
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField label="What is it?" value={label} onChange={e => setLabel(e.target.value)}
            placeholder="e.g. Apartment move-in" sx={{ flex: '2 1 220px' }} />
          <TextField label="Total amount" value={totalAmount} onChange={e => setTotalAmount(e.target.value)}
            placeholder="9000.00" inputProps={{ inputMode: 'decimal' }} sx={{ flex: '1 1 140px' }} />
          <TextField label="Date" type="date" value={date} onChange={e => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px' }} />
        </Box>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Distribution</Typography>

        {rows.map((row) => {
          const rr = resolved.find(r => r.key === row.key);
          return (
            <Box key={row.key} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ flex: '2 1 220px' }}>
                <InputLabel>Category</InputLabel>
                <SearchableSelect value={row.categoryId} label="Category"
                  onChange={e => updateRow(row.key, { categoryId: e.target.value })}>
                  {groupedCategories.map(group => ([
                    <MenuItem key={`hdr-${group.account.id}`} disabled sx={{ fontWeight: 'bold', opacity: 0.7 }}>
                      {group.account.name}
                    </MenuItem>,
                    ...group.cats.map(cat => (
                      <MenuItem key={cat.id} value={cat.id} sx={{ pl: 4 }}
                        disabled={rows.some(r => r.key !== row.key && r.categoryId === cat.id)}>
                        {cat.name}
                      </MenuItem>
                    ))
                  ]))}
                </SearchableSelect>
              </FormControl>
              <ToggleButtonGroup size="small" exclusive value={row.mode}
                onChange={(e, mode) => mode && updateRow(row.key, { mode })}>
                <ToggleButton value="fixed">$</ToggleButton>
                <ToggleButton value="percent">%</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup size="small" exclusive value={row.rowType || 'deduct'}
                onChange={(e, rowType) => rowType && updateRow(row.key, { rowType })}>
                <ToggleButton value="deduct">Deduct</ToggleButton>
                <ToggleButton value="transfer">Transfer</ToggleButton>
              </ToggleButtonGroup>
              <TextField size="small" value={row.value}
                onChange={e => updateRow(row.key, { value: e.target.value })}
                placeholder={row.mode === 'fixed' ? '100.00' : '10'}
                inputProps={{ inputMode: 'decimal' }} sx={{ flex: '1 1 100px' }} />
              {row.mode === 'percent' && rr && (
                <Chip size="small" label={fmt(rr.cents / 100)} variant="outlined" />
              )}
              <IconButton size="small" onClick={() => removeRow(row.key)} disabled={rows.length === 1}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}

        <Button startIcon={<AddIcon />} onClick={addRow} size="small" sx={{ mb: 2 }}>Add category</Button>

        {hasTransferRows && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, p: 1.5,
                     bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ width: '100%' }}>
              Transfer rows move funds into the <b>paying account</b>, which then books the
              one real payment. Pick where the actual money goes out:
            </Typography>
            <FormControl size="small" sx={{ flex: '1 1 180px' }}>
              <InputLabel>Paying account</InputLabel>
              <SearchableSelect value={payerAccountId} label="Paying account"
                onChange={e => { setPayerAccountId(e.target.value); setPayerCategoryId(''); }}>
                {accounts.map(acc => (
                  <MenuItem key={acc.id} value={acc.id}>{acc.name}</MenuItem>
                ))}
              </SearchableSelect>
            </FormControl>
            <FormControl size="small" sx={{ flex: '1 1 200px' }} disabled={payerAccountId === ''}>
              <InputLabel>Paying category</InputLabel>
              <SearchableSelect value={payerCategoryId} label="Paying category"
                onChange={e => setPayerCategoryId(e.target.value)}>
                {categories.filter(c => c.account_id === Number(payerAccountId) && !c.is_deleted)
                  .map(cat => (
                    <MenuItem key={cat.id} value={cat.id}
                      disabled={rows.some(r => r.rowType === 'transfer' && Number(r.categoryId) === cat.id)}>
                      {cat.name}
                    </MenuItem>
                  ))}
              </SearchableSelect>
            </FormControl>
          </Box>
        )}

        <FormControl size="small" fullWidth sx={{ mb: 2 }}>
          <InputLabel>Fallover category (absorbs any remainder — optional)</InputLabel>
          <SearchableSelect value={falloverCategoryId} label="Fallover category (absorbs any remainder — optional)"
            onChange={e => setFalloverCategoryId(e.target.value)}>
            <MenuItem value=""><em>None — allocations must match the total exactly</em></MenuItem>
            {groupedCategories.map(group => ([
              <MenuItem key={`fhdr-${group.account.id}`} disabled sx={{ fontWeight: 'bold', opacity: 0.7 }}>
                {group.account.name}
              </MenuItem>,
              ...group.cats.map(cat => (
                <MenuItem key={cat.id} value={cat.id} sx={{ pl: 4 }}>{cat.name}</MenuItem>
              ))
            ]))}
          </SearchableSelect>
        </FormControl>

        {/* Reconciliation bar */}
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate"
            value={totalCents > 0 ? Math.min(100, (allocatedCents / totalCents) * 100) : 0}
            color={remainingCents < 0 ? 'error' : exact ? 'success' : falloverAbsorbs ? 'info' : 'warning'}
            sx={{ height: 8, borderRadius: 1, mb: 0.5 }} />
          <Typography variant="body2" color={remainingCents < 0 ? 'error' : 'text.secondary'}>
            Allocated {fmt(allocatedCents / 100)} of {fmt(totalCents / 100)}
            {remainingCents > 0 && ` — ${fmt(remainingCents / 100)} remaining${falloverAbsorbs ? ' → fallover' : ''}`}
            {remainingCents < 0 && ` — OVER by ${fmt(-remainingCents / 100)}`}
            {exact && ' — exact ✓'}
          </Typography>
          {remainingCents > 0 && !falloverSet && rows.length > 0 && rows[rows.length - 1].categoryId && (
            <Button size="small" onClick={() => assignRemainderTo(rows[rows.length - 1].key)}>
              Assign remainder to last row
            </Button>
          )}
        </Box>
      </Paper>

      {/* Preview */}
      {previewLines.length > 0 && totalCents > 0 && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Preview</Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Balance now</TableCell>
                  <TableCell align="right">After</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewLines.map((line, i) => (
                  <TableRow key={i}>
                    <TableCell>{line.cat ? accountName(line.cat.account_id) : '—'}</TableCell>
                    <TableCell>
                      {line.cat?.name || '—'}
                      {line.isFallover && <Chip size="small" label="fallover" sx={{ ml: 1 }} />}
                      {line.rowType === 'transfer' && <Chip size="small" color="info" label="transfer → payer" sx={{ ml: 1 }} />}
                      {line.rowType === 'payer' && <Chip size="small" color="primary" label="pays the bill" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell align="right">
                      {line.rowType === 'payer'
                        ? `+${fmt(line.cents / 100)} in, −${fmt(line.cents / 100)} paid`
                        : `−${fmt(line.cents / 100)}`}
                    </TableCell>
                    <TableCell align="right">{fmt(line.before)}</TableCell>
                    <TableCell align="right" sx={{ color: line.negative ? 'error.main' : 'inherit' }}>
                      {fmt(line.after)}{line.negative && ' ⚠'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {hasNegative && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Some categories would go negative. This matches how manual transactions behave,
              but confirm it is intended.
              <FormControlLabel sx={{ display: 'block', mt: 1 }}
                control={<Checkbox checked={confirmNegative} onChange={e => setConfirmNegative(e.target.checked)} />}
                label="I understand — apply anyway" />
            </Alert>
          )}

          <Button variant="contained" size="large" sx={{ mt: 2 }}
            disabled={!canApply || submitting || (hasNegative && !confirmNegative)}
            onClick={handleApply}>
            {submitting ? 'Applying…' : 'Apply this purchase'}
          </Button>
        </Paper>
      )}

      {/* Recent batches */}
      {batches.length > 0 && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Recent purchases</Typography>
          <CollectionSearch label="Search purchase history" value={historySearch} onChange={setHistorySearch}
            total={batches.length} shown={visibleBatches.length} />
          {historySearch && !visibleBatches.length && <Typography>No matching purchases.</Typography>}
          <List dense>
            {visibleBatches.map(b => (
              <ListItem key={b.id}
                secondaryAction={
                  <Button size="small" startIcon={<UndoIcon />} onClick={() => handleUndo(b.id)}>Undo</Button>
                }>
                <ListItemText
                  primary={`${b.label} — ${fmt(b.total_amount)}`}
                  secondary={`${b.transaction_date} · ${b.transaction_count} transactions`} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <AIChatButton context={agentContext} agentic />
    </Container>
  );
}

export default LargePurchasePage;
